<template lang="pug">
.rooms-table
  .rooms-table-title
    h3 {{ $t("property_page.recommended_rent_fee") }}
    div(v-if="needsFeeUpdate")
      button#popover-button-update-notice
        i.fas.fa-exclamation-triangle
      b-popover(target="popover-button-update-notice" triggers="click blur")
        p {{ $t("property_page.some_room_need_update") }}
  .download-recommended-rents
    .recommended-rents
      div.option-item
        div.title {{ $t('property_page.deposit') }}
        b-dropdown#deposit(:text="recommendedRent.deposit.title")
          b-dropdown-item(
            v-for="option in recommendedRentDepositOptions"
            :key="option.value"
            @click="setOption('deposit',option)"
          ) 
            | {{ option.title }}
      div.option-item
        div.title {{ $t('property_page.key_money') }}
        b-dropdown#key-money(:text="recommendedRent.keyMoney.title")
          b-dropdown-item(
            v-for="option in recommendedRentKeyMoneyOptions"
            :key="option.value"
            @click="setOption('keyMoney', option)"
          ) 
            | {{ option.title }}
    div
      download-rooms-info(
        :property="property"
        :recommendedRent="recommendedRent"
      )
  div
    table#rooms-data
      thead
        tr
          td.checkbox-td
            input.room-checkbox(ref='toggleRooms', type="checkbox" @change="toggleSelectedRooms" @click="toggleRoomsCheckboxes" checked)
          td.number {{ $t("room.number") }}
          td.floor {{ $t("property_page.floor") }}
          td.layout {{ $t("property_page.layout") }}
          td.area {{ $t("property_page.area") }}
          td.recommend-rent-fee
            .recommend-rent-info-title
              span {{ $t("property_page.recommend_rent_fee") }}
              i.fas.fa-info#recommend-rent-info
            b-popover(
              target="recommend-rent-info"
              placement="left"
              :content="$t('property_page.recommend_rent_hint')"
              triggers="hover focus"
            )
          td.recommended-rent-basic-unit-price
            | {{ $t("property_page.recommentded_rent_basic_unit_price") }}
          td.current-rent-fee {{ $t("property_page.current_rent_fee") }}
          td.difference {{ $t("property_page.difference") }}
          td.rooms-acts
      tbody
        tr(v-for="room in rooms")
          td.checkbox-td
            input.room-checkbox(type="checkbox" name="room", v-model="selectedRooms", :value='room.id', @change="checkedRoom")
          td.number {{ room.number }}
          td.floor {{ room.floor }}
          td.layout {{ room.layout }}
          td.area {{ $n(room.area) }}
          td.recommend-rent-fee
            b-button(@click="showPopUp(room)") {{ $n(recommendedRentFee(room), 'number') }}
          td.recommended-rent-basic-unit-price
            | {{ basicUnitPrice(recommendedRentFee(room), room.area) }}
          td.current-rent-fee {{ $n(Math.round(room.current_rent_fee), 'number') }}
          td.difference
            span.colored-difference(:class="room.current_rent_fee >= recommendedRentFee(room) ? 'red' : 'blue'") {{ feeDifference(room) }}
          td.rooms-acts
            button.property-room-details(
              :id="'popover-button-details-' + room.id"
            )
              b-tooltip(
                :target="'popover-button-details-' + room.id"
                placement="left"
                :title="tooltip.facilities"
              )
              i.fas.fa-info-circle
            column-popover(
              :facilitiesData='roomFacilities(room)'
              field='code'
              :target="'popover-button-details-' + room.id"
              :placeholder="$t('property_page.no_facilities')"
              :isRoomPopover='true'
              :roomData='room'
            )

            span
              router-link.room-competitors(
                :id="'popover-button-competitor-' + room.id"
                :to="{ name: 'PropertyCompetitors', params: { id: $route.params.id, room_id: room.id, deposit: recommendedRent.deposit.value, keyMoney: recommendedRent.keyMoney.value } }"
              )
                b-tooltip(
                  :target="'popover-button-competitor-' + room.id"
                  placement="left"
                  :title="tooltip.competitors"
                )
                span
                  i.fas.fa-arrow-up
                span
                  i.fas.fa-arrow-down
    factor-price(
      :property="property"
      :room="currentRoom"
      @reset="currentRoom = {}"
    )

</template>

<script>
import ColumnPopover from '@/components/shared/column_popover'
import FactorPrice from '@/pages/Property/Page/factor-price'
import DownloadRoomsInfo from '@/pages/Property/Page/download-rooms-info'
import { isEmpty, debounce } from 'lodash'
import recommendedFeeWithOptions from '@/mixins/recommended-fee-with-options'
import { bus } from '@/bus.js'
import { basicUnitPrice } from '@/helpers/property'

export default {
  components: {
    ColumnPopover,
    FactorPrice,
    DownloadRoomsInfo
  },

  mixins: [recommendedFeeWithOptions],

  props: {
    property: {
      type: Object,
      default() {
        return {}
      }
    },
    rooms: {
      type: Array,
      default() {
        return []
      }
    },
    needsFeeUpdate: {
      type: Boolean,
      default() {
        return false
      }
    }
  },

  data() {
    return {
      selectedRooms: [],
      currentRoom: {},
      tooltip: {
        facilities: this.$t('property_page.tip_facilities'),
        competitors: this.$t('property_page.competitors')
      },
      recommendedRent: {
        deposit: {
          value: 1,
          title: this.$t('property_page.1_month_deposit')
        },
        keyMoney: {
          value: 1,
          title: this.$t('property_page.1_month_key_money')
        }
      }
    }
  },

  computed: {
    recommendedRentDepositOptions() {
      return [
        {
          value: 0,
          title: this.$t('property_page.no_deposit')
        },
        {
          value: 1,
          title: this.$t('property_page.1_month_deposit')
        },
        {
          value: 2,
          title: this.$t('property_page.2_month_deposit')
        }
      ]
    },

    recommendedRentKeyMoneyOptions() {
      return [
        {
          value: 0,
          title: this.$t('property_page.no_key_money')
        },
        {
          value: 1,
          title: this.$t('property_page.1_month_key_money')
        },
        {
          value: 2,
          title: this.$t('property_page.2_month_key_money')
        }
      ]
    }
  },

  created() {
    this.selectAllRooms()

    this.douncedEmit = debounce(() => {
      bus.$emit('annualIncomeOpionsChange', this.recommendedRent)
    }, 3000)
  },

  methods: {
    isEmpty,
    basicUnitPrice,

    showPopUp(room){
      this.currentRoom = room
      this.$bvModal.show('recommended-rent-fee-popup')
    },

    toggleRoomsCheckboxes(source) {
      document.getElementsByName('room').forEach(el => {
        el.checked = source.currentTarget.checked;
      })
    },
    toggleSelectedRooms(source) {
      let checked = source.currentTarget.checked
      if (checked) {
        this.selectAllRooms()
        this.bindCustomEvent('onCheckedRoom', this.selectedRooms)
      } else {
        this.selectedRooms = []
        this.bindCustomEvent('onCheckedRoom', this.selectedRooms)
      }
    },
    feeDifference({ current_rent_fee, recommended_rent_fee }) {
      const recommendedRentFee = this.recommendedRentFee({ recommended_rent_fee })
      const difference = Math.abs(current_rent_fee - recommendedRentFee)

      if (current_rent_fee >= recommendedRentFee) {
        return "+" + difference
      } else {
        return difference
      }
    },
    roomFacilities(room) {
      if (room.facilities) {
        return room.facilities.map(facility => {
          const locale_path = `facilities.${facility.code}`
          return { code: this.$t(locale_path) }
        })
      }
    },
    checkedRoom() {
      this.uncheckToggleRooms();
      this.bindCustomEvent('onCheckedRoom', this.selectedRooms)
    },
    selectAllRooms() {
      for (let i = 0; i < this.rooms.length; i ++) {
        this.selectedRooms.push(this.rooms[i].id)
      }
    },
    bindCustomEvent(customEvent, resource) {
      this.$emit(customEvent, resource)
    },
    uncheckToggleRooms() {
      this.$refs.toggleRooms.checked = !(this.selectedRooms.length == 0)
    },

    recommendedRentFee(room) {
      return Math.round(this.recommendedRentWithOptions(room) / 100) * 100
    },

    setOption(option, { value, title }) {
      this.recommendedRent[option] = { ...this.recommendedRent[option], value, title }
      this.douncedEmit()
    }
  }
}
</script>

<style lang="scss" scoped>
.rooms-table-title {
  align-items: center;
  display: flex;
  margin: 4% 0 2% 0;

  h3 {
    color: #3d4a57;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0;
    margin: 0;
  }

  div {
    button {
      border: 0;
      color: #1ba9eb;
      font-size: 20px;
      opacity: 0.8;
    }
  }
}

.download-recommended-rents {
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  margin: 10px 0 20px 0;

  .recommended-rents {
    align-items: center;
    display: flex;

    .title {
      color: #7c91a7;
      font-size: 13px;
      letter-spacing: 0;
      margin-bottom: 10px;
    }

    .option-item {
      margin-right: 10px;

      ::v-deep button {
        align-items: center;
        background: #fff;
        border: 1px solid #dfe6ee;
        border-radius: 4px;
        color: #313541;
        display: flex;
        font-size: 14px;
        height: 40px;
        justify-content: space-between;
        letter-spacing: 0;
        text-align: left;
        width: 260px;

        &::after {
          color: #7c91a7;
        }
      }

      ::v-deep ul {
        background: #fcfdfe;
        border: 0;
        border-radius: 4px;
        box-shadow: 0 1px 10px 0 rgba(49, 53, 65, 0.2);
        width: 260px;
      }
    }
  }
}

#rooms-data {
  border-left: 1px solid #dfe6ee;
  border-radius: 2px;
  border-right: 1px solid #dfe6ee;
  width: 100%;

  tr {
    background: #fff;
    box-shadow: 0 1px 0 0 #e7ecf4, 0 -1px 0 0 #e7ecf4;
    height: 51px;
  }

  .checkbox-td {
    text-align: center;
    width: 5%;
  }

  .room-checkbox {
    appearance: none;
    background: #fff;
    border: 1px solid #d9dfe7;
    border-radius: 2px;
    color: black;
    display: flex;
    height: 17px;
    margin: 0 auto !important;
    position: relative;
    width: 17px;

    &:checked {
      align-items: center;
      background: #0099fa;
      border-color: #0099fa;
      color: #fff;
      display: flex;
      justify-content: center;
    }

    &:checked::after {
      content: '\f00c';
      font-family: "Font Awesome 5 Free", sans-serif;
      font-size: 11px;
      font-weight: 900;
    }
  }

  thead {
    color: #7c91a7;
    display: table;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0;
    table-layout: fixed;
    width: 100%;

    tr {
      background-color: #fcfdfe;
    }
  }

  tbody {
    color: #313541;
    display: block;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0;
    max-height: 511px;
    overflow: auto;
    padding: 1px 0;

    tr {
      display: table;
      width: 100%;
    }
  }

  .number {
    text-align: left;
    width: 8%;
  }

  .floor {
    text-align: left;
    width: 9%;
  }

  .layout {
    text-align: left;
    width: 5%;
  }

  .area {
    text-align: right;
    width: 6%;
  }

  .recommend-rent-fee {
    text-align: right;
    width: 8%;

    .recommend-rent-info-title {
      position: relative;
      right: -20px;

      i {
        color: red;
        padding: 5px;
      }
    }

    button {
      background-color: #fff;
      border: 0;
      color: #008bd0;
      font-size: 14px;
      letter-spacing: 0;
      padding: 0;
      text-align: right;
    }
  }

  .recommended-rent-basic-unit-price {
    text-align: right;
    width: 8%;
  }

  .current-rent-fee {
    text-align: right;
    width: 8%;
  }

  td.difference {
    text-align: right;
    width: 8%;

    .colored-difference {
      border-radius: 4px;
      font-size: 14px;
      letter-spacing: 0;
      padding: 6px;
      text-align: right;

      &.blue {
        background: #e5f4fe;
        color: #0099fa;
      }

      &.red {
        background: #fceeeb;
        color: #e45c39;
      }
    }
  }

  .rooms-acts {
    padding-right: 25px;
    text-align: right;
    width: 10%;

    .property-room-details {
      background: #fff;
      border: 0;
      color: #7d91a7;
      font-size: 16px;
      margin-right: 15px;
    }

    .room-competitors {
      color: #7d91a7;
      font-size: 16px;
    }
  }
}
</style>
